import * as React from 'react'
import Paper from '@mui/material/Paper'
import IconButton from '@mui/material/IconButton'
import {
  Feed,
  FEED_TYPE_TWITTER,
  FEED_TYPE_FACEBOOK,
  FEED_TYPE_STATUS_IDEAS,
  FEED_TYPE_KEYWORD,
  ContentFeedType
} from 'interfaces'
import { SocialIcon } from 'components/SocialIcon'
import numberToHuman from 'utils/format/numberToHuman'
import { FormattedMessage } from 'react-intl'
import ConfirmDialog from 'components/ConfirmDialog'
import Tooltip from '@mui/material/Tooltip'
import IconDelete from '@mui/icons-material/Close'
import IconAdd from '@mui/icons-material/Add'
import IconCheck from '@mui/icons-material/Check'
import styles from './FeedCard.pcss'
import { NavLink } from 'react-router'

const FEED_TYPES_WITH_STATS = [FEED_TYPE_FACEBOOK, FEED_TYPE_TWITTER, FEED_TYPE_STATUS_IDEAS]

function getFansLabel(type: ContentFeedType, count: number) {
  switch (type) {
    case FEED_TYPE_FACEBOOK:
      return <FormattedMessage id="general.feed.fans" values={{ count }} />
    case FEED_TYPE_TWITTER:
      return <FormattedMessage id="general.feed.followers" values={{ count }} />
    case FEED_TYPE_STATUS_IDEAS:
      return <FormattedMessage id="general.feed.status-ideas" values={{ count }} />
    default:
      return null
  }
}

export interface FeedCardProps {
  feed: Feed
  navigation?: string
  style?: any
  className?: string
  showFansCount?: 'always' | 'hover'
  location?: string
  disableNavigation?: boolean
  deleteConfirmationMessage?: string
  small?: boolean
  noHighlight?: boolean
  selected?: boolean
  onDelete?: (feed: Feed) => void
  onSaveButtonClick?: (feed: Feed) => void
  onClick?: (feed: Feed) => void
  onSelect?: (feed: Feed) => void
}

export function FeedCard(props: FeedCardProps) {
  const ref = React.useRef<HTMLDivElement | null>(null)
  const cardElementRef = React.useRef<HTMLElement | null>(null)

  const onTransitionEnd = React.useCallback((e: any) => {
    const classes = e.target.classList
    const currentScale = (e.target.getBoundingClientRect().width / e.target.offsetWidth)
    const isCollapsed = currentScale.toFixed(1) === '1.0'
    if (classes.contains(styles.raised) && isCollapsed) {
      classes.remove(styles.raised)
    }
  }, [])

  React.useEffect(() => {
    cardElementRef.current = ref.current
    const el = cardElementRef.current
    if (el) {
      el.addEventListener('transitionend', onTransitionEnd)
    }
    return () => {
      if (el) {
        el.removeEventListener('transitionend', onTransitionEnd)
      }
    }
  }, [onTransitionEnd])

  const onMouseEnter = React.useCallback(() => {
    const el = cardElementRef.current
    if (el) {
      el.classList.add(styles.raised)
    }
  }, [])

  const onClick = React.useCallback(() => {
    if (props.onClick) {
      props.onClick(props.feed)
    }
  }, [props])

  const onSelect = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (props.onSelect) {
      props.onSelect(props.feed)
    }
  }, [props])

  const getSocialIcon = React.useCallback(() => {
    const type = props.feed.type
    return <SocialIcon
      icon={type}
      className={styles['s-icon']}
      size={props.small ? '16px' : '24px'}
      outline={type !== FEED_TYPE_KEYWORD}
    />
  }, [props.feed.type, props.small])

  const renderFanCount = React.useCallback(() => {
    const visible = props.showFansCount === 'always'
    const { fanCount, type, itemsCount } = props.feed
    const count = type === FEED_TYPE_STATUS_IDEAS ? itemsCount : fanCount

    if (!FEED_TYPES_WITH_STATS.includes(type)) {
      return null
    }

    const className = `${styles['img-overlay']} ${visible ? styles.visible : ''}`
    const countFormatted = numberToHuman(count || 0)

    return (
      <div className={className}>
        <div className={styles['fan-count']}>
          {
            Boolean(count) && [
              <p key="number" className={styles['fans-number']}>{`${countFormatted}`}</p>,
              <p key="text" className={styles['fans-text']}>{getFansLabel(type, count || 0)}</p>
            ]
          }
        </div>
      </div>
    )
  }, [props.feed, props.showFansCount])

  const onLinkClick = React.useCallback((e: React.MouseEvent) => {
    if (props.disableNavigation) {
      e.preventDefault()
    }
  }, [props.disableNavigation])

  const onDelete = React.useCallback(() => {
    if (props.onDelete) {
      props.onDelete(props.feed)
    }
  }, [props])

  const { uniqueSource, name, type, titleHTML } = props.feed
  const passedClassName = props.className
  const isStatusIdeasCard = type === FEED_TYPE_STATUS_IDEAS
  const isKeywordFeed = type === FEED_TYPE_KEYWORD
  const selected = props.selected

  const feedLink = props.navigation
    ? `${props.navigation}${isStatusIdeasCard ? '/' : '/feed/'}${uniqueSource}`
    : `/content/feeds/${uniqueSource}`

  const navigation = {
    pathname: feedLink,
    state: props.location ? {
      backUrl: props.location
    } : null
  }

  const socialIcon = getSocialIcon()

  const feedName = titleHTML
    ? <span dangerouslySetInnerHTML={{ __html: titleHTML }} />
    : name

  const imageStyles = !isKeywordFeed
    ? { backgroundImage: `url('${props.feed.image}')` }
    : undefined

  const fanCount = renderFanCount()

  return (
    <Paper
      className={`${styles.card} ${passedClassName || ''} ${props.small ? styles.small : ''}`}
      style={props.style}
      ref={ref}
      data-testid="feed-card"
      onMouseEnter={onMouseEnter}
      onClick={onClick}
    >
      {
        props.onDelete && (
          <ConfirmDialog
            message={props.deleteConfirmationMessage || 'Are you sure?'}
            variant="danger"
            confirmLabel={<FormattedMessage id="actions.remove" />}
          >
            {(confirm) => (
              <Tooltip title={<FormattedMessage id="actions.remove" />} placement="top">
                <IconDelete
                  fontSize="small"
                  role="button"
                  className={styles['btn-delete']}
                  onClick={confirm(onDelete)}
                />
              </Tooltip>
            )}
          </ConfirmDialog>
        )
      }
      {
        props.onSelect && (
          <Tooltip
            title={<FormattedMessage id={selected ? 'label.generic.selected' : 'actions.select'} />}
            PopperProps={{
              sx: {
                '&[data-popper-reference-hidden]': {
                  visibility: 'hidden',
                  'pointer-events': 'none'
                }
              }
            }}
          >
            <IconButton
              className={`${styles['btn-add']} ${selected ? styles.selected : ''}`}
              onClick={onSelect}
              size="large"
              data-testid="feed-card-btn-select"
            >
              {selected ? <IconCheck /> : <IconAdd />}
            </IconButton>
          </Tooltip>
        )
      }
      <NavLink to={navigation} className={`${styles['card-link']}`} onClick={onLinkClick}>
        <div className={styles['feed-image']} style={imageStyles}>
          {isKeywordFeed && <p className={styles['keyword-title']}>{feedName}</p>}
          {isKeywordFeed && <div className={`${styles['img-overlay']} ${styles['keyword-overlay']}`}></div>}
          {isStatusIdeasCard && <div className={`${styles['img-overlay']} ${styles['status-overlay']}`}></div>}
          {fanCount}
        </div>
        <div className={`${styles['item-info']} ${styles[type]}`}>
          <h5
            className={`${styles.title} ${props.noHighlight ? styles['no-highlight'] : ''} text-ellipsis`}
            title={name}
          >
            {feedName}
          </h5>
          <p className={styles['feed-type']}>{socialIcon}</p>
        </div>
      </NavLink>
    </Paper>
  )
}

export default FeedCard
